import React, { useEffect, useState } from "react";
import { Cake } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

export const formatBirthday = (b) => {
  if (!b) return "";
  const m = b.match(/(\d{2})-(\d{2})$/);
  return m ? `${Number(m[2])} ${MONTHS[Number(m[1]) - 1]}` : b;
};

export function BirthdayPicker({ day, month, onChange, testPrefix = "auth-birthday" }) {
  return (
    <div>
      <p className="text-xs font-bold text-biga-ink/50 mb-1 flex items-center gap-1"><Cake size={12} /> Birthday (optional) — a treat on your day</p>
      <div className="flex gap-2">
        <Select value={day ? String(day) : ""} onValueChange={(v) => onChange(v, month)}>
          <SelectTrigger data-testid={`${testPrefix}-day`} aria-label="Birthday day"
            className="flex-1 h-12 rounded-xl border-2 border-biga-ink/15 focus:border-biga-orange bg-white font-medium px-3">
            <SelectValue placeholder="Day" />
          </SelectTrigger>
          <SelectContent className="max-h-60 bg-white z-[200]">
            {DAYS.map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={month ? String(month) : ""} onValueChange={(v) => onChange(day, v)}>
          <SelectTrigger data-testid={`${testPrefix}-month`} aria-label="Birthday month"
            className="flex-1 h-12 rounded-xl border-2 border-biga-ink/15 focus:border-biga-orange bg-white font-medium px-3">
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent className="max-h-60 bg-white z-[200]">
            {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export default function BirthdayPrompt() {
  const { user, adoptSession } = useAuth();
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const key = user ? `biga_bday_asked_${user.id}` : "";
  useEffect(() => {
    if (user && (user.role || "customer") === "customer" && user.phone && !user.birthday && ["google", "facebook"].includes(user.provider) && !localStorage.getItem(key)) setOpen(true);
  }, [user, key]);
  const close = () => { localStorage.setItem(key, "1"); setOpen(false); };
  const save = async () => {
    try {
      const { data } = await api.patch("/auth/me", { birthday: `--${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}` });
      adoptSession(data.user || data); toast.success("Birthday saved — see you on your day!"); close();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!user) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="bg-biga-cream rounded-3xl max-w-sm" data-testid="birthday-prompt">
        <DialogTitle className="font-display font-black text-2xl">When's your birthday?</DialogTitle>
        <DialogDescription>Just the day and month. We'll send you a treat.</DialogDescription>
        <BirthdayPicker day={day} month={month} onChange={(d, m) => { setDay(d); setMonth(m); }} testPrefix="birthday-prompt" />
        <div className="flex gap-2">
          <button onClick={close} data-testid="birthday-prompt-skip" className="flex-1 py-3 rounded-full font-bold border-2 border-biga-ink/15">Skip</button>
          <button onClick={save} disabled={!day || !month} data-testid="birthday-prompt-save" className="flex-1 py-3 rounded-full font-bold bg-biga-orange text-biga-cream disabled:opacity-40">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
